import { useMemo, useState, useCallback, useEffect } from 'react';
import { UserProfile, ClubEvent, Attendance, RankRequirement, ProfileChangeRequest } from '../types';
import { calculatePromotionStats, getEligibleRank } from '../lib/promotion';
import { isSystemAdmin, isUserManager } from '../lib/admin';
import { db } from '../lib/firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { 
  Trophy, 
  Calendar, 
  Star, 
  CheckCircle2, 
  Circle, 
  Info,
  Clock,
  MapPin,
  Edit,
  RefreshCcw,
  ShieldCheck,
  FileEdit,
  AlertCircle
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import MemberForm from './MemberForm';
import ProfileChangeRequestModal from './ProfileChangeRequestModal';
import { toast } from 'sonner';

interface ProfileViewProps {
  user: UserProfile;
  events: ClubEvent[];
  attendance: Attendance[];
  ranks: RankRequirement[];
  departments: { id: string, name: string }[];
}

export default function ProfileView({ user, events, attendance, ranks, departments }: ProfileViewProps) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pendingRequests, setPendingRequests] = useState<ProfileChangeRequest[]>([]);

  const isUserAdminOrManager = isUserManager(user);

  useEffect(() => {
    if (!user?.uid) return;
    const q = query(
      collection(db, 'profile_change_requests'),
      where('userId', '==', user.uid),
      where('status', '==', 'pending')
    );
    const unsub = onSnapshot(q, (snap) => {
      setPendingRequests(snap.docs.map(d => ({ id: d.id, ...d.data() } as ProfileChangeRequest)));
    });
    return () => unsub();
  }, [user?.uid]);
  
  const stats = useMemo(() => {
    if (!user) return { totalPoints: 0, eventCount: 0, monthsAsMember: 0, attendedEvents: [] } as any;
    return calculatePromotionStats(user, events, attendance);
  }, [user, events, attendance]);

  const eligibleRank = useMemo(() => {
    if (!user) return { id: '', name: '', shortName: '', order: 0 } as any;
    return getEligibleRank(user, stats, ranks);
  }, [user, stats, ranks]);
  
  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    // Since we use onSnapshot, data is already live. 
    // This button provides visual confirmation and ensures memoized values are fresh.
    setTimeout(() => {
      setIsRefreshing(false);
      toast.success('Personnel data synchronized with Starfleet Command');
    }, 800);
  }, []);

  if (!user) return null;

  const currentRankIndex = ranks.findIndex(r => r.id === user.currentRank || (user.currentRank === 'CDR' && r.id === 'CMDR'));
  const currentRankInfo = currentRankIndex !== -1 ? ranks[currentRankIndex] : ranks[0] || { shortName: user.currentRank, name: user.currentRank };
  const nextRankIndex = currentRankIndex + 1;
  const nextRank = nextRankIndex < ranks.length ? ranks[nextRankIndex] : null;

  const eligibleRankIndex = ranks.findIndex(r => r.id === eligibleRank.id);
  const isPromotionEligible = eligibleRankIndex > currentRankIndex;

  const isGuest = user.roles?.includes('guest');
  const hidePromotionTrack = isGuest && (!user.shipName || !user.shipName.toLowerCase().includes('zavala'));

  return (
    <div className="space-y-8">
      {/* Pending XO Review Alert Banner */}
      {pendingRequests.length > 0 && (
        <div className="bg-lcars-gold/15 border-2 border-lcars-gold p-4 rounded-xl flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Clock className="w-6 h-6 text-lcars-gold animate-pulse shrink-0" />
            <div>
              <div className="font-bold text-sm text-lcars-gold uppercase tracking-wider">
                Profile Change Request Pending XO Approval
              </div>
              <p className="text-xs text-[var(--foreground)]/70 font-sans mt-0.5">
                Your submitted personnel file updates are currently in the Executive Officer review queue. Changes will take effect once approved.
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsRequestModalOpen(true)}
            className="lcars-pill bg-lcars-gold text-black px-4 py-1 text-xs font-bold shrink-0 hover:bg-white transition-colors"
          >
            UPDATE REQUEST
          </button>
        </div>
      )}

      {/* Promotion Status Panel */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1 bg-[var(--background)] border-l-4 lg:border-l-8 border-lcars-gold pl-3 lg:pl-4 py-2">
          <div className="flex flex-wrap items-center gap-2 lg:gap-3 mb-6">
            <div className="bg-lcars-gold text-black px-3 lg:px-4 py-1 rounded-full font-bold text-lg lg:text-2xl whitespace-nowrap">
              {user.firstName} {user.lastName}
            </div>
            <div className="bg-lcars-red text-[var(--foreground)] px-3 py-1 rounded-full font-bold text-sm">
              {currentRankInfo.shortName}
            </div>
            {user.departments && user.departments.length > 0 ? (
              user.departments.map(d => (
                <div key={d} className="bg-lcars-blue text-black px-3 py-1 rounded-full font-bold text-sm uppercase">
                  {d}
                </div>
              ))
            ) : user.department && (
              <div className="bg-lcars-blue text-black px-3 py-1 rounded-full font-bold text-sm uppercase">
                {user.department}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <button 
                onClick={() => setIsRequestModalOpen(true)}
                className="lcars-pill bg-lcars-gold text-black px-4 py-1 text-xs font-bold hover:bg-white transition-colors flex items-center gap-1.5"
                title="Submit changes to the XO review queue"
              >
                <FileEdit className="w-3.5 h-3.5" /> REQUEST PROFILE CHANGES
              </button>
              {isUserAdminOrManager && (
                <button 
                  onClick={() => setIsEditModalOpen(true)}
                  className="lcars-pill bg-lcars-blue text-black px-4 py-1 text-xs font-bold hover:bg-white transition-colors"
                  title="Direct Administrator Edit"
                >
                  DIRECT ADMIN EDIT
                </button>
              )}
              <button 
                onClick={handleRefresh}
                disabled={isRefreshing}
                className={`lcars-pill bg-lcars-purple text-black px-4 py-1 text-xs font-bold ${isRefreshing ? 'animate-spin' : ''}`}
              >
                SYNC DATA
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4">
            <div className="bg-lcars-blue/10 p-3 border-t-2 border-lcars-blue">
              <div className="text-lcars-blue text-[0.6rem] lg:text-[0.7rem] font-bold uppercase">Points</div>
              <div className="text-xl lg:text-2xl font-bold">{stats.totalPoints}</div>
            </div>
            <div className="bg-lcars-gold/10 p-3 border-t-2 border-lcars-gold">
              <div className="text-lcars-gold text-[0.6rem] lg:text-[0.7rem] font-bold uppercase">Missions</div>
              <div className="text-xl lg:text-2xl font-bold">{stats.eventCount}</div>
            </div>
            <div className="bg-lcars-purple/10 p-3 border-t-2 border-lcars-purple">
              <div className="text-lcars-purple text-[0.6rem] lg:text-[0.7rem] font-bold uppercase">Tenure</div>
              <div className="text-xl lg:text-2xl font-bold">{stats.monthsAsMember} MO</div>
            </div>
            <div className="bg-lcars-tan/10 p-3 border-t-2 border-lcars-tan">
              <div className="text-lcars-tan text-[0.6rem] lg:text-[0.7rem] font-bold uppercase">Cell Phone</div>
              <div className="text-sm lg:text-base font-bold truncate">{user.cell || '---'}</div>
            </div>
            <div className="bg-lcars-tan/10 p-3 border-t-2 border-lcars-tan">
              <div className="text-lcars-tan text-[0.6rem] lg:text-[0.7rem] font-bold uppercase">SCC Number</div>
              <div className="text-xl lg:text-2xl font-bold truncate">{user.sccNumber || '---'}</div>
            </div>
          </div>
        </div>
        
        {isPromotionEligible && nextRank && !hidePromotionTrack && (
          <div className="w-full lg:w-48 bg-lcars-red text-[var(--foreground)] p-4 lg:p-6 rounded-r-full lg:rounded-r-full flex flex-row lg:flex-col justify-center lg:justify-center items-center text-center animate-pulse gap-4 lg:gap-2">
            <div className="font-bold text-base lg:text-xl leading-tight uppercase">Promotion Eligible</div>
            <div className="text-base lg:text-sm font-bold bg-white/20 px-3 py-1 rounded-full lg:bg-transparent lg:p-0">{nextRank.shortName}</div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Promotion Progress */}
        {!hidePromotionTrack && (
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center gap-2">
              <div className="h-8 w-32 bg-lcars-blue rounded-l-full"></div>
              <div className="flex-1 h-8 bg-lcars-blue flex items-center px-4">
                <span className="font-bold text-black">PROMOTION TRACK: {nextRank?.name || 'MAX RANK'}</span>
              </div>
            </div>
            
            <div className="bg-[var(--background)] border-l-8 border-lcars-blue pl-6 py-4 space-y-6">
              {nextRank ? (
                <div className="space-y-6">
                  <div className="space-y-6">
                    {/* Tenure Requirement */}
                    {nextRank.minMonths !== undefined && (
                      <RequirementRow 
                        label="TENURE (MONTHS)" 
                        current={stats.monthsAsMember} 
                        required={nextRank.minMonths} 
                        color="lcars-purple"
                      />
                    )}
                    
                    {/* Points Requirement */}
                    {nextRank.minPoints !== undefined && (
                      <RequirementRow 
                        label="PROMOTION POINTS" 
                        current={stats.totalPoints} 
                        required={nextRank.minPoints} 
                        color="lcars-gold"
                      />
                    )}
                    
                    {/* Events Requirement */}
                    {nextRank.minEvents !== undefined && (
                      <RequirementRow 
                        label="MISSIONS ATTENDED" 
                        current={stats.eventCount} 
                        required={nextRank.minEvents} 
                        color="lcars-blue"
                      />
                    )}

                    {/* OTS/OCC Requirements */}
                    {(nextRank.otsRequired || nextRank.occRequired) && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                        {nextRank.otsRequired && (
                          <div className={`p-3 rounded-full flex items-center justify-between border-2 ${user.ots ? 'bg-lcars-gold/20 border-lcars-gold text-lcars-gold' : 'bg-[var(--background)] border-lcars-blue text-lcars-blue'}`}>
                            <span className="text-xs font-bold px-4">OTS PASSED</span>
                            <div className="pr-2">
                              {user.ots ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                            </div>
                          </div>
                        )}
                        {nextRank.occRequired && (
                          <div className={`p-3 rounded-full flex items-center justify-between border-2 ${user.occ ? 'bg-lcars-gold/20 border-lcars-gold text-lcars-gold' : 'bg-[var(--background)] border-lcars-blue text-lcars-blue'}`}>
                            <span className="text-xs font-bold px-4">OCC PASSED</span>
                            <div className="pr-2">
                              {user.occ ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 bg-lcars-gold/10 rounded-lg border-2 border-lcars-gold">
                  <Trophy className="w-16 h-16 text-lcars-gold mx-auto mb-4" />
                  <p className="text-2xl font-bold text-lcars-gold">MAXIMUM RANK ACHIEVED</p>
                  <p className="text-[var(--foreground)] text-sm mt-2">YOU HAVE REACHED THE HIGHEST RANK IN THE USS ZAVALA.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Recent Service History */}
        <div className={`space-y-4 ${hidePromotionTrack ? 'lg:col-span-3' : ''}`}>
          <div className="flex items-center gap-2">
            <div className="h-8 w-24 bg-lcars-purple rounded-l-full"></div>
            <div className="flex-1 h-8 bg-lcars-purple flex items-center px-4">
              <span className="font-bold text-black">MISSION LOGS</span>
            </div>
          </div>
          
          <div className="bg-[var(--background)] border-l-8 border-lcars-purple overflow-hidden">
            <div className="max-h-[500px] overflow-auto">
              <table className="w-full border-collapse">
                <tbody className="divide-y divide-lcars-purple/20">
                  {stats.attendedEvents.slice(0, 15).map(event => (
                    <tr key={event.id} className="hover:bg-lcars-purple/10 transition-colors">
                      <td className="p-4">
                        <div className="font-bold text-lcars-purple">{event.name}</div>
                        <div className="text-[0.65rem] text-[var(--foreground)]/60 mt-1">
                          {event.startDate && !isNaN(parseISO(event.startDate).getTime()) ? format(parseISO(event.startDate), 'yyyy.MM.dd') : 'TBD'} | {event.type}
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        <span className="bg-lcars-blue text-black px-2 py-0.5 rounded-full font-bold text-[0.7rem]">
                          +{event.points}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {stats.attendedEvents.length === 0 && (
                    <tr>
                      <td className="p-8 text-center text-[var(--foreground)]/40 text-sm italic">
                        NO SERVICE RECORD FOUND.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Request Changes Modal */}
      <ProfileChangeRequestModal
        isOpen={isRequestModalOpen}
        onClose={() => setIsRequestModalOpen(false)}
        currentUserProfile={user}
        departments={departments}
      />

      {/* Admin Direct Edit Modal */}
      <MemberForm 
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        member={user}
        currentUserProfile={user}
        ranks={ranks}
        departments={departments}
      />

      {/* Security Clearance Diagnostics */}
      <div className="bg-lcars-purple/10 border-l-8 border-lcars-purple p-6 rounded-r-full mt-12">
        <div className="flex items-center gap-4 mb-4">
          <ShieldCheck className="w-6 h-6 text-lcars-purple" />
          <h3 className="text-xl font-bold text-lcars-purple uppercase tracking-tighter">Security Clearance Diagnostics</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
          <div className="space-y-2">
            <p className="text-[var(--foreground)]/40 font-bold tracking-widest uppercase text-[0.6rem]">Authenticated Email</p>
            <p className="text-[var(--foreground)] font-mono bg-[var(--background)]/40 px-3 py-1 rounded border border-white/10">{user.email}</p>
          </div>
          <div className="space-y-2">
            <p className="text-[var(--foreground)]/40 font-bold tracking-widest uppercase text-[0.6rem]">Membership Stardates</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-[var(--background)]/40 p-2 border border-white/5">
                <p className="text-[0.5rem] opacity-40 uppercase">Zavala Join</p>
                <p className="font-mono text-xs">{user.zavalaJoinDate}</p>
              </div>
              <div className="bg-[var(--background)]/40 p-2 border border-white/5">
                <p className="text-[0.5rem] opacity-40 uppercase">Promotion Date</p>
                <p className="font-mono text-xs">{user.promotionRankDate}</p>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-[var(--foreground)]/40 font-bold tracking-widest uppercase text-[0.6rem]">Assigned Roles</p>
            <div className="flex flex-wrap gap-2">
              {user.roles?.map(role => (
                <span key={role} className="bg-lcars-purple text-black px-3 py-0.5 rounded-full font-bold text-[0.7rem] uppercase">
                  {role}
                </span>
              )) || <span className="text-[var(--foreground)]/20 italic">No roles assigned</span>}
            </div>
          </div>
        </div>
        <div className="mt-6 pt-4 border-t border-white/10">
          <p className="text-[0.6rem] text-[var(--foreground)]/30 font-bold tracking-widest uppercase">
            System Admin Status: {isSystemAdmin(user) ? 'VERIFIED' : 'NOT DETECTED'}
          </p>
        </div>
      </div>
    </div>
  );
}

function RequirementRow({ label, current, required, color }: { label: string, current: number, required: number, color: string }) {
  const percent = Math.min(100, (current / required) * 100);
  const isComplete = current >= required;

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-end">
        <span className={`text-[0.7rem] font-bold text-${color}`}>{label}</span>
        <span className={`text-xl font-bold ${isComplete ? 'text-[var(--foreground)]' : 'text-[var(--foreground)]/40'}`}>
          {current} <span className="text-[0.7rem] opacity-40">/ {required}</span>
        </span>
      </div>
      <div className="h-4 bg-white/10 rounded-full overflow-hidden flex">
        <div 
          className={`h-full transition-all duration-1000 bg-${color} rounded-r-full`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

function Rocket(props: any) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
      <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
      <path d="M9 12H4s.55-3.03 2-5c1.62-2.2 5-3 5-3" />
      <path d="M12 15v5s3.03-.55 5-2c2.2-1.62 3-5 3-5" />
    </svg>
  );
}
